// Green Screen Yapper Ad's script editor (live's `script-section` and its parts:
// `script-names-fields`, `creator-fields`, `clip-header`, `line-details`, `card-preview`; spec
// G14-green-screen-yapper, PR h): the names, the creator, the ten lines by clip with each clip's
// beat, each point's card line, and the card as it will read. The plan's lengths and counts, the
// approval issue and the warnings are the server's (`GreenScreenDraft.plan`, `script_issue`,
// `script_problems`), read on each autosave. While revising (PR f4), only the refused clips' lines
// and beats stay open.
import { ActionIcon, Group, Loader, NumberInput, Select, Text, TextInput, Tooltip } from "@mantine/core";
import { WandSparkles } from "lucide-react";

import type {
  GreenScreenGender,
  GreenScreenLine,
  GreenScreenPlan,
  GreenScreenRole,
  GreenScreenScript,
  GreenScreenSloganSource,
} from "../../../api/generated/model";
import { GENERATE_SCRIPT as GENERATE_SCRIPT_LABEL } from "../../../platform/scripts/ScriptActions";
import { FieldLabel } from "../../../ui/FieldLabel";
import podcast from "./podcast.module.css";
import classes from "./green-screen.module.css";

/** Live's words (`library-view/constants.ts`, `messages.ts`). */
export const SCRIPT_LABEL = "Script, card & creator";
export const SCRIPT_TOOLTIP =
  "Approval 1: the hook (clip 1), points one to three (clip 2), points four and five (clip 3), the " +
  "bad habit, the product, the usage line and the slogan (clip 4), the card line of each point, the " +
  "creator’s hand action per clip and their look. Nothing is generated before you approve.";
const NAMES_HEADING = "names · card title · slogan (the hook, line 8 and line 10 are built from these)";
const DESIGNED_CREATOR_HEADING =
  "creator · designed for this product's buyer · plain heather grey crew-neck t-shirt, framing and " +
  "voice fixed by the format";
const PHOTO_CREATOR_HEADING =
  "creator · the person you picked, described as seen · own clothes · the voice follows gender and age";
const CARD_HEADING = "the card · on screen the whole ad, top third";
const DESIGNED_LOOK_PLACEHOLDER =
  "A 22-year-old female creator with … (age 20–25, face, build, hair, skin tone, skin detail, makeup; no clothing)";
const PHOTO_LOOK_PLACEHOLDER = "A 34-year-old man with … (as seen: age, face, build, hair, skin tone, clothes)";
const EMPTY_HINT = "to let the script director write the five-things script, the card and the creator from the product page.";
const CARD_MAX_CHARACTERS = 28;
const AGE_LIMITS = { min: 16, max: 80 } as const;
const META_SEPARATOR = " · ";
export const LOCKED_LINE_TOOLTIP =
  "Only the refused clip can be changed now: the images and the other clips are kept.";
const CLIP_LIST_SEPARATOR = ", ";
const WARNING_MARK = "⚠";

const ROLE_LABELS: Readonly<Record<GreenScreenRole, string>> = {
  hook: "hook",
  point: "point",
  bad_habit: "bad habit",
  product: "the product",
  usage: "usage",
  slogan: "slogan",
};
const CLIP_NOTES: Readonly<Record<number, string>> = {
  1: "clip 1 · the hook",
  2: "clip 2 · points one to three",
  3: "clip 3 · points four and five",
  4: "clip 4 · bad habit, the product, usage, slogan",
};
const SLOGAN_SOURCES: Readonly<Record<GreenScreenSloganSource, string>> = {
  input: "your slogan",
  page: "from the page",
  written: "written from the page",
};
const GENDERS: readonly { value: GreenScreenGender; label: string }[] = [
  { value: "female", label: "female" },
  { value: "male", label: "male" },
];
/** The point lines, in order (live's `POINT_LINE_IDS`). */
const POINT_LINE_IDS: readonly number[] = [2, 3, 4, 5, 6];
const TITLE_END = " should do";

/** "clips 5 · 8 · 6 · 10 s · 29 s · 77 words" (live's `toScriptMetaLabel`). */
export function scriptMeta(plan: GreenScreenPlan): string {
  return [
    `clips ${plan.clips.map((clip) => clip.duration_s).join(META_SEPARATOR)} s`,
    `${plan.total_s} s`,
    `${plan.words} words`,
  ].join(META_SEPARATOR);
}

/** Live's `toRevisionNote`: what Revise opens, for one refused clip or several. */
export function revisionNote(clips: readonly number[]): string {
  const list = clips.join(CLIP_LIST_SEPARATOR);
  if (clips.length === 1) {
    return (
      `The video model refused clip ${list}. Change its lines or its beat and approve: only this ` +
      "clip is made again, the images and the other clips stay."
    );
  }
  return (
    `The video model refused clips ${list}. Change their lines or beats and approve: only these ` +
    "clips are made again, the images and the other clips stay."
  );
}

/** "hook", "point 3", "the product" (live's `toLineRoleLabel`). */
function roleLabel(line: GreenScreenLine): string {
  if (line.role !== "point") return ROLE_LABELS[line.role];
  return `${ROLE_LABELS.point} ${POINT_LINE_IDS.indexOf(line.id) + 1}`;
}

/** The card title on its two rows (live's `toCardTitleRows`). */
export function cardTitleRows(title: string): string[] {
  const trimmed = title.trim();
  const at = trimmed.toLowerCase().lastIndexOf(TITLE_END);
  return at > 0 ? [trimmed.slice(0, at).trim(), trimmed.slice(at + 1).trim()] : [trimmed];
}

/** The five card lines, points 1 to 5 (live's `toCardLines`). */
export function cardLines(script: GreenScreenScript): string[] {
  return POINT_LINE_IDS.map((id) => script.lines.find((line) => line.id === id)?.card_line.trim() ?? "");
}

/** Live's `CardPreview`: the title on its rows over the five numbered lines. */
export function CardPreview({ script }: { script: GreenScreenScript }) {
  return (
    <div className={classes.card} aria-label="Card preview">
      {cardTitleRows(script.card_title).map((row) => (
        <Text key={row} className={classes.cardTitle}>
          {row}
        </Text>
      ))}
      {cardLines(script).map((cardLine, at) => (
        <Text key={at} className={classes.cardLine}>
          {`${at + 1}. ${cardLine}`}
        </Text>
      ))}
    </div>
  );
}

function Heading({ label }: { label: string }) {
  return <Text className={classes.heading}>{label}</Text>;
}

interface RowProps {
  label: string;
  value: string;
  placeholder?: string;
  disabled: boolean;
  /** Why the row is locked, on hover (live's locked line while revising). */
  lockedTip?: string | null;
  suffix?: React.ReactNode;
  onChange: (value: string) => void;
}

/** Live's `LabelledLineInput`: a label, the line, and what follows it (a count, a source). */
function Row({ label, value, placeholder, disabled, lockedTip = null, suffix, onChange }: RowProps) {
  const input = (
    <TextInput
      className={classes.input}
      variant="unstyled"
      size="xs"
      aria-label={label}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );
  return (
    <Group gap={8} wrap="nowrap" align="flex-start" className={classes.row}>
      <Text className={classes.label}>{label}</Text>
      {lockedTip === null ? (
        input
      ) : (
        <Tooltip label={lockedTip} multiline maw={280} withArrow>
          <div className={classes.input}>{input}</div>
        </Tooltip>
      )}
      {suffix}
    </Group>
  );
}

export interface GreenScreenScriptProps {
  script: GreenScreenScript | null;
  plan: GreenScreenPlan | null;
  /** A person is picked: the creator is described as seen, not designed. */
  photo: boolean;
  writing: boolean;
  locked: boolean;
  issue: string | null;
  problems: readonly string[];
  /** The refused clips Revise opened (live's `revisedClips`); none unless revising. */
  revised?: readonly number[];
  onGenerate: () => void;
  onChange: (script: GreenScreenScript) => void;
}

/**
 * Live's script section: "Script, card & creator" with its tooltip, the plan's meta line and the
 * wand; without a script, live's hint; with one, the names, the creator, the lines by clip (each
 * clip's header with its seconds and words, and its beat), each line's role, words and, for a
 * point, its card line against 28 characters; the card as it will read; approval 1's issue in
 * red and the director's checks as warnings.
 */
export function GreenScreenScriptSection(props: GreenScreenScriptProps) {
  const { script, plan, photo, writing, issue, problems, revised = [], onGenerate, onChange } = props;
  const revisingNow = revised.length > 0;
  // While revising, all but the refused clips' lines and beats is kept as approved.
  const locked = props.locked || revisingNow;
  const clipLocked = (clip: number) => props.locked || (revisingNow && !revised.includes(clip));
  const lockedTip = (clip: number) => (revisingNow && !revised.includes(clip) ? LOCKED_LINE_TOOLTIP : null);
  const set = (change: Partial<GreenScreenScript>) => {
    if (script !== null) onChange({ ...script, ...change });
  };
  const setLine = (id: number, change: Partial<GreenScreenLine>) => {
    if (script === null) return;
    set({ lines: script.lines.map((line) => (line.id === id ? { ...line, ...change } : line)) });
  };
  const setBeat = (clip: number, action: string) => {
    if (script === null) return;
    set({ beats: script.beats.map((beat) => (beat.clip === clip ? { ...beat, action } : beat)) });
  };
  const words = (id: number) => plan?.lines.find((line) => line.id === id)?.words ?? null;
  return (
    <section aria-label="Script">
      <div className={podcast.scriptHeader}>
        <Text component="div" className={podcast.scriptTitle}>
          <FieldLabel label={SCRIPT_LABEL} info={SCRIPT_TOOLTIP} />
        </Text>
        <Group gap={8} wrap="nowrap">
          {plan === null ? null : <Text className={podcast.meta}>{scriptMeta(plan)}</Text>}
          <Tooltip label={GENERATE_SCRIPT_LABEL} withArrow>
            <ActionIcon variant="subtle" size={36} aria-label={GENERATE_SCRIPT_LABEL} disabled={locked} onClick={onGenerate}>
              <WandSparkles size={16} aria-hidden />
            </ActionIcon>
          </Tooltip>
        </Group>
      </div>
      {revisingNow ? <Text className={classes.note}>{revisionNote(revised)}</Text> : null}
      <div className={podcast.editor} data-busy={writing || undefined}>
        {script === null ? (
          <Text className={podcast.hint}>
            Press
            <WandSparkles size={14} className={podcast.hintIcon} aria-label="the wand" />
            {EMPTY_HINT}
          </Text>
        ) : (
          <div className={classes.script}>
            <Heading label={NAMES_HEADING} />
            <Row label="Brand says" placeholder="Hero" value={script.brand_spoken} disabled={locked} onChange={(value) => set({ brand_spoken: value })} />
            <Row label="Product says" placeholder="Slow Roast" value={script.product_spoken} disabled={locked} onChange={(value) => set({ product_spoken: value })} />
            <Row label="Category" placeholder="pimple patch" value={script.category} disabled={locked} onChange={(value) => set({ category: value })} />
            <Row label="Card title" placeholder="What a Pimple Patch Should Do" value={script.card_title} disabled={locked} onChange={(value) => set({ card_title: value })} />
            <Row
              label="Slogan"
              placeholder="The brand's slogan"
              value={script.slogan}
              disabled={locked}
              suffix={<Text className={classes.source}>{SLOGAN_SOURCES[script.slogan_source]}</Text>}
              onChange={(value) => set({ slogan: value })}
            />
            <Heading label={photo ? PHOTO_CREATOR_HEADING : DESIGNED_CREATOR_HEADING} />
            <Row
              label="Creator"
              placeholder={photo ? PHOTO_LOOK_PLACEHOLDER : DESIGNED_LOOK_PLACEHOLDER}
              value={script.creator.look}
              disabled={locked}
              onChange={(look) => set({ creator: { ...script.creator, look } })}
            />
            <Group gap={8} wrap="nowrap" className={classes.row}>
              <Text className={classes.label}>Gender</Text>
              <Select
                size="xs"
                w={96}
                aria-label="Gender"
                data={GENDERS}
                allowDeselect={false}
                value={script.creator.gender}
                disabled={locked}
                onChange={(gender) => {
                  if (gender === "female" || gender === "male") set({ creator: { ...script.creator, gender } });
                }}
              />
              <Text className={classes.label}>Age</Text>
              <NumberInput
                size="xs"
                w={64}
                aria-label="Age"
                min={AGE_LIMITS.min}
                max={AGE_LIMITS.max}
                allowDecimal={false}
                value={script.creator.age}
                disabled={locked}
                onChange={(age) => {
                  if (typeof age === "number") set({ creator: { ...script.creator, age } });
                }}
              />
            </Group>
            <Row
              label="Why"
              placeholder="Why this person fits the buyer"
              value={script.creator.why}
              disabled={locked}
              onChange={(why) => set({ creator: { ...script.creator, why } })}
            />
            {script.lines.map((line, index) => {
              const first = index === 0 || script.lines[index - 1]?.clip !== line.clip;
              const planned = plan?.clips.find((clip) => clip.clip === line.clip) ?? null;
              const beat = script.beats.find((each) => each.clip === line.clip);
              const note = CLIP_NOTES[line.clip] ?? `clip ${line.clip}`;
              const count = words(line.id);
              const characters = [...line.card_line.trim()].length;
              return (
                <div key={line.id} className={classes.line}>
                  {first ? (
                    <>
                      <Heading
                        label={
                          planned === null
                            ? note
                            : [note, `${planned.duration_s} s`, `${planned.words} words`].join(META_SEPARATOR)
                        }
                      />
                      {beat === undefined ? null : (
                        <Row
                          label="Beat"
                          placeholder="The hands and face in this clip, tied to the words"
                          value={beat.action}
                          disabled={clipLocked(line.clip)}
                          lockedTip={lockedTip(line.clip)}
                          onChange={(action) => setBeat(line.clip, action)}
                        />
                      )}
                    </>
                  ) : null}
                  <Text className={classes.role}>{roleLabel(line)}</Text>
                  <Row
                    label="Says:"
                    placeholder="Exactly what the creator says"
                    value={line.spoken}
                    disabled={clipLocked(line.clip)}
                    lockedTip={lockedTip(line.clip)}
                    suffix={count === null ? null : <Text className={podcast.count}>{`${count}w`}</Text>}
                    onChange={(spoken) => setLine(line.id, { spoken })}
                  />
                  {line.role === "point" ? (
                    <Row
                      label="Card:"
                      placeholder="Card line, 2–6 words, ends with a period"
                      value={line.card_line}
                      disabled={locked}
                      suffix={
                        <Text className={podcast.count} data-over={characters > CARD_MAX_CHARACTERS || undefined}>
                          {`${characters}/${CARD_MAX_CHARACTERS}`}
                        </Text>
                      }
                      onChange={(card) => setLine(line.id, { card_line: card })}
                    />
                  ) : null}
                </div>
              );
            })}
            <Heading label={CARD_HEADING} />
            <CardPreview script={script} />
          </div>
        )}
        {writing ? (
          <div className={podcast.editorLoader}>
            <Loader size="sm" aria-label="Writing the script" />
          </div>
        ) : null}
      </div>
      {issue === null || writing ? null : <Text className={podcast.issue}>{issue}</Text>}
      {problems.map((problem) => (
        <Text key={problem} className={podcast.warning}>
          {`${WARNING_MARK} ${problem}`}
        </Text>
      ))}
    </section>
  );
}
